'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import classNames from 'classnames';
import NavPanel from '@/components/Nav/NavPanel';
import { headerLinks } from './data';
import { useRouter, usePathname } from 'next/navigation';
import Chevron from '../../../public/assets/icons/chevron-right.svg';
import ChevronLeft from '../../../public/assets/icons/chevron-left.svg';
import ChevronDown from '../../../public/assets/icons/chevron-down.svg';
import Close from '../../../public/assets/icons/close.svg';
import { ReadMoreButton } from '@/components/common/ReadMoreButton';
import Button from '@/components/common/Button';
import MobileNavPanel from '@/components/Nav/MobileNavPanel';
import {
  INavChild,
  isNavWithChild,
  useCurrentPath,
} from '@/components/Nav/utilities';
import DoubleDeckerMenu from '@/app/(regular-layout)/DoubleDeckerMenu';
import Logo from '../../../public/assets/logo/logo.png';
import { Route } from 'next';

// The Figma header (node 10186:17618) is 48px tall in total, hairline
// included, so the content row runs 47px and the rule below it makes up the
// last pixel. `top` is the full 48: it is what the nav panels and the
// announcement bar hang off.
const topMenu = {
  height: 'h-[2.9375rem]',
  top: 'top-12',
};

export default function NavigationBar({ notFound }: { notFound?: boolean }) {
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeNavPanel, setActiveNavPanel] = useState<string>('');
  const [openCategory, setOpenCategory] = useState<INavChild | null>(null);
  const currentPath = useCurrentPath();
  const pathname = usePathname();

  const handleMenuToggle = () => {
    setMenuOpen((prev) => !prev);
    setOpenCategory(null);
  };

  const handleActiveNavPanel = (name: string) => {
    setActiveNavPanel((prev) => (prev === name ? '' : name));
  };

  const closeActiveNavPanel = () => activeNavPanel && setActiveNavPanel('');

  const handleNavigation = (href: Route) => {
    router.push(href);
    setMenuOpen(false);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setOpenCategory(null);
  };

  useEffect(() => {
    setActiveNavPanel('');
    setMenuOpen(false);
    setOpenCategory(null);
  }, [pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (activeNavPanel) {
        setActiveNavPanel('');
      }

      if (menuOpen) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [menuOpen, activeNavPanel]);

  return (
    <nav
      className="text-primary sticky top-0 z-[5000] w-full bg-white"
      onClick={closeActiveNavPanel}
    >
      <div
        className={classNames([
          'fixed inset-0 z-10 hidden transform bg-neutral-900/50 transition-all duration-500 md:block',
          !activeNavPanel &&
            'pointer-events-none invisible opacity-0 delay-100',
        ])}
      />
      <div className="relative z-20 w-full border-b border-background-3 bg-white">
        <div
          className={classNames([
            'relative mx-auto flex max-w-view items-center bg-white px-4',
            topMenu.height,
          ])}
        >
          <div className="flex items-center max-md:shrink-0 max-md:grow">
            <Link href="/" title="Homepage">
              <Image
                src={Logo}
                alt="Logo"
                priority
                className="block h-auto w-[125px]"
              />
            </Link>
          </div>
          {/* Desktop Nav */}
          <div className="mx-8 hidden h-4 w-[1px] bg-background-3 md:block" />
          <ul className="hidden h-full grow items-center md:flex">
            {headerLinks.map((link) => {
              const hasNavPanel = isNavWithChild(link);
              return (
                <li key={link.name} className="relative h-full">
                  <p
                    className={classNames([
                      'relative flex h-full cursor-pointer items-center gap-[6px] text-nowrap border-b border-b-transparent px-4 py-3 transition-colors hover:bg-[#E8E8E8]',
                      hasNavPanel &&
                        currentPath?.baseName === link.name &&
                        !notFound &&
                        'bg-background shadow-[0_1px_0_0_#9EA2AD]',
                    ])}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActiveNavPanel(link.name);
                    }}
                  >
                    {hasNavPanel ? (
                      <span>{link.name}</span>
                    ) : (
                      <Link href={link.href}>{link.name}</Link>
                    )}
                    {hasNavPanel && (
                      <ChevronDown
                        className={classNames(
                          'transition duration-200',
                          activeNavPanel === link.name && '-scale-y-100'
                        )}
                      />
                    )}
                  </p>
                  {hasNavPanel && (
                    <NavPanel
                      topClass={topMenu.top}
                      link={link}
                      active={activeNavPanel}
                      onClose={(href) => {
                        if (href === pathname) {
                          closeActiveNavPanel();
                        }
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
          <div className="block md:hidden">
            <Link
              style={{ fontSize: '14px' }}
              href="/contact"
              className="icon-button"
            >
              <Image
                src="/assets/icons/nav/icon-message.svg"
                alt="chat"
                width={20}
                height={20}
              />
            </Link>
          </div>

          <div className="hidden gap-10 md:flex">
            <span
              className="hidden items-center gap-[6px] lg:flex"
            >
              <Image
                src="/assets/icons/nav/phone.svg"
                alt="call"
                width={16}
                height={16}
              />
              (800) 897-9066
            </span>
            <Button href="/contact" variant="nav">
              Contact
            </Button>
          </div>
          <button
            onClick={handleMenuToggle}
            data-collapse-toggle="navbar-hamburger"
            type="button"
            className="icon-button md:hidden"
            aria-controls="navbar-hamburger"
            aria-expanded={menuOpen}
          >
            <span className="sr-only">
              {menuOpen ? 'Close main menu' : 'Open main menu'}
            </span>
            <svg
              className="h-5 w-5"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 17 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M1 1h15M1 7h15M1 13h15"
              />
            </svg>
          </button>
          {/* Mobile Nav */}
          {menuOpen && (
            <div className="fixed inset-0 flex flex-col overflow-hidden bg-white md:hidden">
              <div className="flex bg-[#71768480]">
                <button
                  className="bg-background p-2 text-[#1F2228]"
                  onClick={() => closeMenu()}
                >
                  <Close />
                </button>
              </div>
              {!openCategory && (
                <ul className="flex flex-col">
                  {headerLinks.map((link, index) => {
                    const hasNavPanel = isNavWithChild(link);
                    return (
                      <li key={index}>
                        <button
                          onClick={() =>
                            hasNavPanel
                              ? setOpenCategory(link)
                              : handleNavigation(link.href)
                          }
                          className="flex w-full items-center justify-between border-b border-background-4 px-4 py-3"
                          aria-current="page"
                        >
                          <span>{link.name}</span>{' '}
                          {hasNavPanel && (
                            <Chevron className="text-icon-muted" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {openCategory && (
                <>
                  <button
                    onClick={() => setOpenCategory(null)}
                    className="flex w-full items-center gap-1 border-b border-background-4 px-4 py-3 text-primary-default"
                    aria-current="page"
                  >
                    <ChevronLeft />
                    <span>Back</span>
                  </button>
                  <MobileNavPanel
                    items={openCategory.items}
                    closeMenu={closeMenu}
                    categoryName={openCategory.name}
                  />
                </>
              )}
              <div className="flex flex-1 flex-col justify-end gap-1 p-4">
                <ReadMoreButton
                  href="/ai-classification"
                  text="Explore more solutions"
                  className="w-full justify-between"
                  onClick={closeMenu}
                />
                <Button
                  href="/contact"
                  variant="outlined"
                  className="w-full justify-between"
                  onClick={closeMenu}
                >
                  Request a demo
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {currentPath?.doubleDeckerLinks && !notFound && (
        <DoubleDeckerMenu
          menuItems={currentPath.doubleDeckerLinks}
          key={currentPath.doubleDeckerKey}
        />
      )}
    </nav>
  );
}
